import { useState, useEffect, useCallback } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, LabelList, ResponsiveContainer } from 'recharts';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  getAdminStats,
  getAdminOrderStats,
  getAdminMenuItems,
  getAdminMenuCategories,
} from '../lib/api';
import { todayMexicoYmd } from '../lib/dateMexico';
import { cn } from '../lib/utils';
import {
  Calendar,
  Package,
  MessageSquare,
  Star,
  Clock,
  CheckCircle,
  ClipboardList,
  Settings,
  Truck,
  ShoppingBag,
  AlertTriangle,
} from 'lucide-react';

const PIZAS_MODE = process.env.REACT_APP_PIZAS_MODE === '1';

// admin-dashboard-20261020: rangos del dashboard (días calendario de México)
const RANGOS = [
  { key: 'hoy', label: 'Hoy', corto: 'hoy' },
  { key: '3d', label: 'Últimos 3 días', corto: 'últimos 3 días' },
  { key: '7d', label: 'Últimos 7 días', corto: 'últimos 7 días' },
  { key: 'mes', label: 'Mes actual', corto: 'mes actual' },
];

function addDaysYmd(ymd, n) {
  const [y, m, d] = ymd.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d + n));
  return dt.toISOString().slice(0, 10);
}

export function rangoFechas(key, hoy = todayMexicoYmd()) {
  if (key === '3d') return { from: addDaysYmd(hoy, -2), to: hoy };
  if (key === '7d') return { from: addDaysYmd(hoy, -6), to: hoy };
  if (key === 'mes') return { from: `${hoy.slice(0, 8)}01`, to: hoy };
  return { from: hoy, to: hoy };
}

const MESES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
const DIAS = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
function etiquetaDia(ymd, larga) {
  const [y, m, d] = ymd.split('-').map(Number);
  const wd = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
  return larga ? `${DIAS[wd]} ${d}` : `${d} ${MESES[m - 1]}`;
}
const pesos = (v) => `$${Number(v || 0).toLocaleString('es-MX', { maximumFractionDigits: 0 })}`;
const pesosCorto = (v) => {
  const n = Number(v || 0);
  if (n >= 1000) return `$${(n / 1000).toLocaleString('es-MX', { maximumFractionDigits: 1 })}k`;
  return `$${n.toLocaleString('es-MX', { maximumFractionDigits: 0 })}`;
};

function IngresosChart({ data, total, rangoLabel }) {
  const muchos = data.length > 10;
  const filas = data.map((d) => ({ ...d, etiqueta: etiquetaDia(d.date, !muchos) }));
  return (
    <Card className="border border-border shadow-ambient" data-testid="ingresos-chart">
      <CardHeader className="pb-2">
        <div className="flex flex-wrap items-end justify-between gap-2">
          <div>
            <CardTitle className="font-heading text-sm uppercase tracking-wider text-muted-foreground">
              Ingresos · {rangoLabel}
            </CardTitle>
            <p className="mt-1 font-heading text-3xl font-bold tabular-nums text-foreground">{pesos(total)}</p>
          </div>
          <p className="text-xs text-muted-foreground">Pedidos no cancelados, por día (hora de México)</p>
        </div>
      </CardHeader>
      <CardContent className="pt-2">
        <div className="h-72 w-full" role="img" aria-label={`Gráfica de ingresos por día, ${rangoLabel}, total ${pesos(total)}`}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={filas} margin={{ top: 28, right: 8, left: 0, bottom: 4 }}>
              <CartesianGrid vertical={false} stroke="hsl(var(--border))" strokeDasharray="3 3" />
              <XAxis
                dataKey="etiqueta"
                tickLine={false}
                axisLine={{ stroke: 'hsl(var(--border))' }}
                tick={{ fill: 'hsl(var(--foreground))', fontSize: 12 }}
                interval={muchos ? 'preserveStartEnd' : 0}
                minTickGap={4}
              />
              <YAxis
                tickFormatter={pesosCorto}
                tickLine={false}
                axisLine={false}
                width={56}
                tick={{ fill: 'hsl(var(--foreground))', fontSize: 12, opacity: 0.7 }}
              />
              <Tooltip
                cursor={{ fill: 'hsl(var(--primary) / 0.08)' }}
                formatter={(v, _n, item) => [`${pesos(v)} · ${item?.payload?.count || 0} pedido(s)`, 'Ingresos']}
                labelFormatter={(l) => l}
                contentStyle={{ borderRadius: 12, border: '1px solid hsl(var(--border))', fontSize: 13 }}
              />
              <Bar dataKey="total" fill="hsl(var(--primary))" radius={[6, 6, 0, 0]} maxBarSize={56} isAnimationActive={false}>
                <LabelList
                  dataKey="total"
                  position="top"
                  formatter={(v) => (Number(v) > 0 ? pesosCorto(v) : '')}
                  style={{ fill: 'hsl(var(--foreground))', fontSize: muchos ? 10 : 12, fontWeight: 600 }}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rango, setRango] = useState('hoy');
  const [cargandoRango, setCargandoRango] = useState(false);

  const loadStats = useCallback(async (key) => {
    try {
      if (PIZAS_MODE) {
        const { from, to } = rangoFechas(key);
        const [orderStats, menuItems, menuCategories] = await Promise.all([
          getAdminOrderStats({ from, to }),
          getAdminMenuItems(),
          getAdminMenuCategories(),
        ]);
        setStats({
          orders_today: Number(orderStats?.orders_today || 0),
          pending_review: Number(orderStats?.pending || 0),
          in_kitchen: Number(orderStats?.in_kitchen || 0),
          pending_payment: Number(orderStats?.pending_payment || 0),
          menu_items: Array.isArray(menuItems) ? menuItems.length : 0,
          menu_categories: Array.isArray(menuCategories) ? menuCategories.length : 0,
          online_sales_total: Number(orderStats?.online_sales_total || 0),
          online_sales_count: Number(orderStats?.online_sales_count || 0),
          low_stock_count: Number(orderStats?.low_stock_count || 0),
          low_stock_items: Array.isArray(orderStats?.low_stock_items) ? orderStats.low_stock_items : [],
          daily_revenue: Array.isArray(orderStats?.daily_revenue) ? orderStats.daily_revenue : [],
          revenue_total: Number(orderStats?.revenue_total || 0),
        });
      } else {
        const data = await getAdminStats();
        setStats(data);
      }
    } catch (error) {
      console.error('Error loading stats:', error);
    } finally {
      setLoading(false);
      setCargandoRango(false);
    }
  }, []);

  useEffect(() => {
    loadStats(rango);
  }, [rango, loadStats]);

  const elegirRango = (key) => {
    if (key === rango) return;
    setCargandoRango(true);
    setRango(key);
  };
  const rangoActual = RANGOS.find((r) => r.key === rango) || RANGOS[0];
  const sufijo = rango === 'hoy' ? 'de hoy' : `· ${rangoActual.corto}`;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-muted-foreground">Cargando estadísticas...</div>
      </div>
    );
  }

  const statCards = PIZAS_MODE
    ? [
        {
          title: rango === 'hoy' ? 'Pedidos de Hoy' : `Pedidos ${sufijo}`,
          value: stats?.orders_today || 0,
          icon: ClipboardList,
          color: 'bg-primary/10 text-primary',
        },
        {
          title: 'Por Revisar',
          value: stats?.pending_review || 0,
          icon: Clock,
          color: 'bg-warning/10 text-warning',
        },
        {
          title: 'En Cocina',
          value: stats?.in_kitchen || 0,
          icon: CheckCircle,
          color: 'bg-success/10 text-success',
        },
        {
          title: 'Pago Pendiente',
          value: stats?.pending_payment || 0,
          icon: Calendar,
          color: 'bg-accent/10 text-accent',
        },
        {
          title: `Ventas en línea (${rangoActual.corto})`,
          value: `$${Number(stats?.online_sales_total || 0).toLocaleString('es-MX')}`,
          subtitle: `${stats?.online_sales_count || 0} pedido(s) Web / WhatsApp / Teléfono`,
          icon: ShoppingBag,
          color: 'bg-primary/10 text-primary',
        },
        {
          title: 'Pizzas en Menú',
          value: stats?.menu_items || 0,
          icon: Package,
          color: 'bg-sage-300/20 text-sage-500',
        },
        {
          title: 'Categorías',
          value: stats?.menu_categories || 0,
          icon: Truck,
          color: 'bg-primary/10 text-primary',
        },
      ]
    : [
        {
          title: 'Citas de Hoy',
          value: stats?.appointments?.today || 0,
          icon: Clock,
          color: 'bg-primary/10 text-primary',
        },
        {
          title: 'Citas Pendientes',
          value: stats?.appointments?.pending || 0,
          icon: Calendar,
          color: 'bg-warning/10 text-warning',
        },
        {
          title: 'Total Citas',
          value: stats?.appointments?.total || 0,
          icon: CheckCircle,
          color: 'bg-success/10 text-success',
        },
        {
          title: 'Productos Activos',
          value: stats?.products?.active || 0,
          icon: Package,
          color: 'bg-sage-300/20 text-sage-500',
        },
        {
          title: 'Reseñas Pendientes',
          value: stats?.reviews?.pending || 0,
          icon: MessageSquare,
          color: 'bg-accent/10 text-accent',
        },
        {
          title: 'Calificación Promedio',
          value: stats?.reviews?.avg_rating || '0.0',
          icon: Star,
          color: 'bg-warning/10 text-warning',
        },
      ];

  return (
    <div data-testid="admin-dashboard">
      <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-foreground">Dashboard</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {PIZAS_MODE ? 'Resumen operativo de pedidos, cocina y menú.' : 'Bienvenido al panel de administración'}
          </p>
        </div>
        {PIZAS_MODE ? (
          <div
            role="group"
            aria-label="Periodo del resumen"
            className="inline-flex flex-wrap gap-1 self-start rounded-xl border border-border bg-card p-1 shadow-sm md:self-auto"
            data-testid="dashboard-rango"
          >
            {RANGOS.map((r) => {
              const activo = r.key === rango;
              return (
                <button
                  key={r.key}
                  type="button"
                  onClick={() => elegirRango(r.key)}
                  aria-pressed={activo}
                  data-testid={`rango-${r.key}`}
                  className={cn(
                    'min-h-[40px] cursor-pointer rounded-lg px-3.5 text-sm font-semibold transition-colors duration-150',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                    activo
                      ? 'bg-primary text-primary-foreground shadow-sm'
                      : 'text-foreground/75 hover:bg-primary/10 hover:text-foreground'
                  )}
                >
                  {r.label}
                </button>
              );
            })}
          </div>
        ) : null}
      </div>

      <div
        className={cn(
          'grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3 transition-opacity duration-200',
          cargandoRango && 'opacity-60'
        )}
        aria-busy={cargandoRango}
      >
        {statCards.map((card, index) => (
          <Card
            key={index}
            className="group border border-border shadow-ambient transition-[box-shadow,transform] duration-200 hover:-translate-y-0.5 hover:shadow-ambient-lg motion-reduce:transform-none"
            data-testid={`stat-card-${index}`}
          >
            <CardContent className="p-5">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{card.title}</p>
                  <p className="mt-2 font-heading text-3xl font-bold tabular-nums text-foreground">{card.value}</p>
                  {card.subtitle ? (
                    <p className="mt-1 text-xs text-muted-foreground">{card.subtitle}</p>
                  ) : null}
                </div>
                <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${card.color}`}>
                  <card.icon className="h-5 w-5" aria-hidden="true" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {PIZAS_MODE ? (
        <div className={cn('mt-6 transition-opacity duration-200', cargandoRango && 'opacity-60')}>
          <IngresosChart data={stats?.daily_revenue || []} total={stats?.revenue_total || 0} rangoLabel={rangoActual.label} />
        </div>
      ) : null}

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="border border-border shadow-ambient">
          <CardHeader>
            <CardTitle className="font-heading">Acciones Rápidas</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {PIZAS_MODE ? (
              <>
                <Link to="/admin/pedidos" className="block cursor-pointer rounded-xl border border-transparent bg-primary/5 p-4 transition-colors duration-150 hover:border-primary/20 hover:bg-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                  <div className="flex items-center gap-3">
                    <ClipboardList className="w-5 h-5 text-primary" />
                    <div>
                      <p className="font-medium text-foreground">Bandeja de Pedidos</p>
                      <p className="text-sm text-muted-foreground">Aceptar, rechazar y mover estado de cocina</p>
                    </div>
                  </div>
                </Link>
                <Link to="/admin/menu" className="block cursor-pointer rounded-xl border border-transparent bg-primary/5 p-4 transition-colors duration-150 hover:border-primary/20 hover:bg-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                  <div className="flex items-center gap-3">
                    <Package className="w-5 h-5 text-primary" />
                    <div>
                      <p className="font-medium text-foreground">Gestionar Menú</p>
                      <p className="text-sm text-muted-foreground">Pizzas, categorías, ingredientes e imágenes</p>
                    </div>
                  </div>
                </Link>
                <Link to="/admin/config-tienda" className="block cursor-pointer rounded-xl border border-transparent bg-primary/5 p-4 transition-colors duration-150 hover:border-primary/20 hover:bg-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                  <div className="flex items-center gap-3">
                    <Settings className="w-5 h-5 text-primary" />
                    <div>
                      <p className="font-medium text-foreground">Configurar Tienda</p>
                      <p className="text-sm text-muted-foreground">Radio de entrega, tiempos y mensajes</p>
                    </div>
                  </div>
                </Link>
              </>
            ) : (
              <>
                <Link to="/admin/productos" className="block cursor-pointer rounded-xl border border-transparent bg-primary/5 p-4 transition-colors duration-150 hover:border-primary/20 hover:bg-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                  <div className="flex items-center gap-3">
                    <Package className="w-5 h-5 text-primary" />
                    <div>
                      <p className="font-medium text-foreground">Gestionar Productos</p>
                      <p className="text-sm text-muted-foreground">Agregar, editar o eliminar servicios</p>
                    </div>
                  </div>
                </Link>
                <Link to="/admin/citas" className="block cursor-pointer rounded-xl border border-transparent bg-primary/5 p-4 transition-colors duration-150 hover:border-primary/20 hover:bg-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                  <div className="flex items-center gap-3">
                    <Calendar className="w-5 h-5 text-primary" />
                    <div>
                      <p className="font-medium text-foreground">Ver Citas</p>
                      <p className="text-sm text-muted-foreground">Confirmar o cancelar citas</p>
                    </div>
                  </div>
                </Link>
                <Link to="/admin/resenas" className="block cursor-pointer rounded-xl border border-transparent bg-primary/5 p-4 transition-colors duration-150 hover:border-primary/20 hover:bg-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                  <div className="flex items-center gap-3">
                    <MessageSquare className="w-5 h-5 text-primary" />
                    <div>
                      <p className="font-medium text-foreground">Moderar Reseñas</p>
                      <p className="text-sm text-muted-foreground">Aprobar o rechazar comentarios</p>
                    </div>
                  </div>
                </Link>
              </>
            )}
          </CardContent>
        </Card>

        <Card className="border border-border shadow-ambient">
          <CardHeader>
            <CardTitle className="font-heading">{PIZAS_MODE ? 'Almacén' : 'Información del Sistema'}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {PIZAS_MODE ? (
              <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground">Insumos con stock bajo</p>
                    <Link
                      to="/admin/almacen?low=1"
                      className="inline-block mt-2 text-4xl font-bold text-amber-900 hover:text-amber-950 tabular-nums"
                      title="Ver almacén"
                    >
                      {stats?.low_stock_count ?? 0}
                    </Link>
                    {stats?.low_stock_items?.length > 0 ? (
                      <p className="text-xs text-muted-foreground mt-2 leading-relaxed">
                        {stats.low_stock_items.map((i) => i.name).join(' · ')}
                        {(stats?.low_stock_count || 0) > stats.low_stock_items.length
                          ? ` · +${stats.low_stock_count - stats.low_stock_items.length} más`
                          : ''}
                      </p>
                    ) : (
                      <p className="text-xs text-muted-foreground mt-2">Todo el almacén está por encima del mínimo.</p>
                    )}
                  </div>
                </div>
              </div>
            ) : null}
            {/* En modo pizzería los totales ya aparecen en las tarjetas superiores; no se repiten aquí. */}
            {!PIZAS_MODE ? (
            <>
            <div className="flex justify-between items-center py-2 border-b border-border">
              <span className="text-muted-foreground">{PIZAS_MODE ? 'Pedidos hoy' : 'Total Productos'}</span>
              <span className="font-medium">{PIZAS_MODE ? stats?.orders_today || 0 : stats?.products?.total || 0}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-border">
              <span className="text-muted-foreground">{PIZAS_MODE ? 'Por revisar' : 'Total Reseñas'}</span>
              <span className="font-medium">{PIZAS_MODE ? stats?.pending_review || 0 : stats?.reviews?.total || 0}</span>
            </div>
            {PIZAS_MODE ? (
              <div className="flex justify-between items-center py-2 border-b border-border">
                <span className="text-muted-foreground">Ventas en línea (hoy)</span>
                <span className="font-medium text-right">
                  ${Number(stats?.online_sales_total || 0).toLocaleString('es-MX')}
                  <span className="block text-xs text-muted-foreground font-normal">
                    {stats?.online_sales_count || 0} pedidos
                  </span>
                </span>
              </div>
            ) : null}
            <div className="flex justify-between items-center py-2">
              <span className="text-muted-foreground">{PIZAS_MODE ? 'Pizzas en menú' : 'Total Citas'}</span>
              <span className="font-medium">{PIZAS_MODE ? stats?.menu_items || 0 : stats?.appointments?.total || 0}</span>
            </div>
            </>
            ) : null}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
